import {unixTimestampMSFromUTCString} from '@wandb/weave/common/util/time';
import {Nullable} from '@wandb/weave/common/util/types';
import {ApolloQueryResult} from 'apollo-client';
import React, {useEffect, useMemo, useState} from 'react';

import {ProjectListViewMode} from '../../components/ExpandableProjectList';
import {
  EntityProfileQuery,
  useEntityProfileQuery,
  useProfilePageQuery,
} from '../../generated/graphql';
import {useViewer} from '../../state/viewer/hooks';
import {useAdminModeActive} from '../../util/admin';
import {useGallerySpec} from '../../util/gallery';
import {LoaderProxy} from './../../containers/AppLoader';
import {
  ENTITY_PAGE_INITIAL_STATE,
  ENTITY_PAGE_STATE_CURRENT_VERSION,
  EntityPageContext,
  EntityPageContextType,
  EntityPageSection,
  EntityPageState,
  EntityPageUpdaterContext,
  ShowcasedLink,
} from './context';
import {EntityPageData} from './useEntityPageData';
import {
  parseProfileSpec,
  useAutosaveProfileView,
  useCreateEntityProfile,
} from './util';

type EntityPageContextProviderProps = {
  entityName: string;
  entityPageData: EntityPageData;
};

export const EntityPageContextProvider: React.FC<EntityPageContextProviderProps> =
  React.memo(({entityName, entityPageData, children}) => {
    const viewer = useViewer();
    const adminMode = useAdminModeActive();

    const entityExists =
      entityPageData.data != null && !entityPageData.data.available;
    const entityIsTeam = entityPageData.data?.isTeam ?? false;

    const isOwnPersonalEntity = entityName === viewer?.username;
    const editable = entityIsTeam
      ? entityPageData.isTeamMemberOrAdmin
      : isOwnPersonalEntity || adminMode;
    const readOnly = !editable;

    // Data fetching

    const {
      loading: loadingEntityProfile,
      data: entityProfileData,
      refetch: refetchEntityProfile,
    } = useEntityProfileQuery({
      variables: {entityName},
    });
    const profileViewData = entityProfileData?.entity?.profileView;
    const profileViewID = profileViewData?.id ?? null;
    const entityProfileUpdatedAtMS = profileViewData?.updatedAt
      ? unixTimestampMSFromUTCString(profileViewData.updatedAt)
      : null;

    const {loading: loadingProfilePage, data: profilePageData} =
      useProfilePageQuery({
        variables: {username: entityName},
      });

    const {loading: loadingGallerySpec, gallerySpec} = useGallerySpec();

    const loading =
      entityPageData.loading ||
      loadingEntityProfile ||
      loadingProfilePage ||
      loadingGallerySpec;

    // Create new entity profile if it doesn't exist yet

    const createEntityProfile = useCreateEntityProfile({
      profilePageData,
      entityIsTeam,
      gallerySpec,
      entityName,
    });

    useEffect(() => {
      if (loading || readOnly || !entityExists) {
        return;
      }
      const spec = profileViewData?.spec;
      if (spec != null) {
        return;
      }

      (async () => {
        await createEntityProfile();
        await refetchEntityProfile();
      })();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    // State to be rendered

    const stateFromServerOrDefault: EntityPageState | null = useMemo(() => {
      const spec: Nullable<string> = profileViewData?.spec;
      const stateFromServer = spec != null ? parseProfileSpec(spec) : null;
      if (stateFromServer != null) {
        return stateFromServer;
      }

      // Handle read-only viewers arriving at a profile page
      // which hasn't been created yet
      if (!loading && (readOnly || !entityExists)) {
        return ENTITY_PAGE_INITIAL_STATE;
      }

      return null;
    }, [profileViewData, loading, readOnly, entityExists]);

    // Render

    if (loading || stateFromServerOrDefault == null) {
      return <LoaderProxy name="entity-page-context-provider-loader" />;
    }

    return (
      <EntityPageContextProviderLoaded
        {...{
          readOnly,
          isOwnPersonalEntity,
          profileViewID,
          entityName,
          entityIsTeam,
          stateFromServer: stateFromServerOrDefault,
          entityProfileUpdatedAtMS,
          refetchEntityProfile,
        }}>
        {children}
      </EntityPageContextProviderLoaded>
    );
  });

type EntityPageContextProviderLoadedProps = {
  readOnly: boolean;
  isOwnPersonalEntity: boolean;
  profileViewID: string | null;
  entityName: string;
  entityIsTeam: boolean;
  stateFromServer: EntityPageState;
  entityProfileUpdatedAtMS: number | null;
  refetchEntityProfile: () => Promise<ApolloQueryResult<EntityProfileQuery>>;
};

const EntityPageContextProviderLoaded: React.FC<EntityPageContextProviderLoadedProps> =
  React.memo(
    ({
      readOnly,
      isOwnPersonalEntity,
      profileViewID,
      entityName,
      entityIsTeam,
      stateFromServer,
      entityProfileUpdatedAtMS,
      refetchEntityProfile,
      children,
    }) => {
      // Context value state variables

      const {
        reportSpec: reportSpecFromServer,
        sectionOrder: sectionOrderFromServer,
        projectListViewMode: projectListViewModeFromServer,
        showcasedProjectIDs: showcasedProjectIDsFromServer,
        showcasedReportIDs: showcasedReportIDsFromServer,
        links: linksFromServer,
      } = stateFromServer;

      const [reportSpec, setReportSpec] = useState(reportSpecFromServer);

      const [sectionOrder, setSectionOrder] = useState<EntityPageSection[]>(
        sectionOrderFromServer
      );

      const [projectListViewMode, setProjectListViewMode] =
        useState<ProjectListViewMode>(projectListViewModeFromServer);

      const [showcasedProjectIDs, setShowcasedProjectIDs] = useState<string[]>(
        showcasedProjectIDsFromServer
      );

      const [showcasedReportIDs, setShowcasedReportIDs] = useState<string[]>(
        showcasedReportIDsFromServer
      );

      const [links, setLinks] = useState<ShowcasedLink[]>(linksFromServer);

      // Context value

      const entityPageState: EntityPageState = useMemo(
        () => ({
          version: ENTITY_PAGE_STATE_CURRENT_VERSION,
          reportSpec,
          sectionOrder,
          projectListViewMode,
          showcasedProjectIDs,
          showcasedReportIDs,
          links,
        }),
        [
          reportSpec,
          sectionOrder,
          projectListViewMode,
          showcasedProjectIDs,
          showcasedReportIDs,
          links,
        ]
      );

      const entityPageContextValue: EntityPageContextType = useMemo(() => {
        return {
          ...entityPageState,

          // HAX: We're ignoring the user-customized section order for now
          sectionOrder: [`reports`, `projects`, `links`, `activity`, `runs`],

          profileViewID: profileViewID ?? ``,
          entityName,
          entityIsTeam,
          readOnly,
          isOwnPersonalEntity,
        };
      }, [
        entityPageState,
        profileViewID,
        entityName,
        entityIsTeam,
        readOnly,
        isOwnPersonalEntity,
      ]);

      const updaters = useMemo(
        () => ({
          setReportSpec,
          setSectionOrder,
          setProjectListViewMode,
          setShowcasedProjectIDs,
          setShowcasedReportIDs,
          setLinks,
        }),
        []
      );

      // Autosave on state change

      const conflictWarning = useAutosaveProfileView({
        entityName,
        profileViewID,
        entityProfileUpdatedAtMS,
        readOnly,
        entityPageState,
        refetchEntityProfile,
      });

      return (
        <EntityPageContext.Provider value={entityPageContextValue}>
          <EntityPageUpdaterContext.Provider value={updaters}>
            {conflictWarning}
            {children}
          </EntityPageUpdaterContext.Provider>
        </EntityPageContext.Provider>
      );
    }
  );
